<script setup>
import { computed, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    paymentRequest: {
        type: Object,
        default: () => null
    },
    order: {
        type: Object,
        default: () => null
    },
    selectedCompany: {
        type: Object,
        default: () => null
    },
    selectedBankAccount: {
        type: Object,
        default: () => null
    },
    finalPaymentLink: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['update:visible', 'proceed', 'cancel']);

const paymentRequestData = computed(() => {
    if (props.paymentRequest) {
        return props.paymentRequest;
    }
    
    if (props.order?.payment_request) {
        return props.order.payment_request;
    }
    
    if (props.order && !props.paymentRequest && !props.order.payment_request) {
        return {
            company_profile_id: props.order.company_profile_id,
            bank_account_id: props.order.bank_account_id,
            payment_link: props.order.payment_link,
            company: props.order.company,
            bank_account: props.order.bankAccount || props.order.bank_account,
            sent_at: props.order.created_at || new Date().toISOString()
        };
    }
    
    return null;
});

const hasPaymentRequestDivergence = computed(() => {
    if (!paymentRequestData.value) return false;
    
    const companyDiverges = props.selectedCompany?.id !== paymentRequestData.value.company_profile_id;
    const bankAccountDiverges = props.selectedBankAccount?.id !== paymentRequestData.value.bank_account_id;
    const paymentLinkDiverges = props.finalPaymentLink !== paymentRequestData.value.payment_link;
    
    return companyDiverges || bankAccountDiverges || paymentLinkDiverges;
});

const paymentRequestDivergenceMessage = computed(() => {
    if (!hasPaymentRequestDivergence.value || !paymentRequestData.value) return '';
    
    const sentDate = paymentRequestData.value.sent_at ? new Date(paymentRequestData.value.sent_at).toLocaleDateString() : $t('unknown date');
    
    if (props.paymentRequest || props.order?.payment_request) {
        return $t('The information does not match the payment request sent on {date}. Do you want to continue anyway?').replace('{date}', sentDate);
    }
    
    return $t('The information does not match the existing invoice data. Do you want to continue anyway?');
});

const closeModal = () => {
    emit('update:visible', false);
};

const proceedWithDivergence = () => {
    emit('proceed');
    closeModal();
};

const cancelDivergence = () => {
    emit('cancel');
    closeModal();
};
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="emit('update:visible', $event)"
        :style="{ width: '500px' }"
        :header="$t('Payment Information Mismatch')"
        :modal="true"
    >
        <div class="flex items-start gap-4">
            <i class="pi pi-exclamation-triangle text-3xl text-orange-500" />
            <div class="flex-1">
                <p class="mb-4">{{ paymentRequestDivergenceMessage }}</p>
                
                <div v-if="paymentRequestData" class="space-y-4">
                    <!-- Original Payment Request -->
                    <div class="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p class="font-semibold text-blue-800 mb-2">
                            {{ paymentRequest || order?.payment_request ? $t('Original Payment Request') : $t('Existing Invoice Data') }}:
                        </p>
                        <div class="text-sm text-blue-700 space-y-1">
                            <div v-if="paymentRequestData.company">
                                <strong>{{ $t('Company') }}:</strong> {{ paymentRequestData.company.name }}
                            </div>
                            <div v-if="paymentRequestData.bank_account">
                                <strong>{{ $t('Bank Account') }}:</strong> {{ paymentRequestData.bank_account.name }}
                            </div>
                            <div v-if="paymentRequestData.payment_link">
                                <strong>{{ $t('Payment Link') }}:</strong> {{ paymentRequestData.payment_link }}
                            </div>
                        </div>
                    </div>

                    <!-- Current Selections -->
                    <div class="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p class="font-semibold text-amber-800 mb-2">{{ $t('Current Selections') }}:</p>
                        <div class="text-sm text-amber-700 space-y-1">
                            <div v-if="selectedCompany">
                                <strong>{{ $t('Company') }}:</strong> {{ selectedCompany.name }}
                            </div>
                            <div v-if="selectedBankAccount">
                                <strong>{{ $t('Bank Account') }}:</strong> {{ selectedBankAccount.name }}
                            </div>
                            <div v-if="finalPaymentLink">
                                <strong>{{ $t('Payment Link') }}:</strong> {{ finalPaymentLink }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <template #footer>
            <Button
                :label="$t('Cancel')"
                severity="secondary"
                @click="cancelDivergence"
            />
            <Button
                :label="$t('Continue Anyway')"
                severity="warning"
                @click="proceedWithDivergence"
            />
        </template>
    </Dialog>
</template>